import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { CheckCircle2, CircleDashed, AlertCircle, FileText, RefreshCw, Package, Bell, MailX, Ban, RotateCcw, Trash2, Check, X, Gift } from "lucide-react";
import { toast } from "sonner";
import CclLetterCard from "@/components/app/CclLetterCard";
import OnboardingStepPresets from "@/components/app/OnboardingStepPresets";
import GiftorsManager from "@/components/app/onboarding/GiftorsManager";
import { downloadAuthed } from "@/lib/api";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const STEP_LABELS = {
  payment: "Payment on account",
  identity: "ID / AML check",
  ccl: "Client Care Letter",
  questionnaires: "Client questionnaires",
  ta: "TA (property) forms",
  funds: "Proof of funds",
};
const STEP_SHORT = {
  payment: "Pay", identity: "ID", ccl: "CCL",
  questionnaires: "Forms", ta: "TA", funds: "Funds",
};
const STEP_ORDER = ["payment", "identity", "ccl", "questionnaires", "ta", "funds"];

/**
 * Solicitor-only read-only view of a case's 5-step onboarding wizard.
 * Mirrors what each client sees but flattens all clients into one grid
 * so the fee-earner can spot the laggard without impersonating anyone.
 */
export default function OnboardingStatusPanel({ caseId }) {
  const [data, setData] = useState(null);
  const [funds, setFunds] = useState(null);
  const [disabledSteps, setDisabledSteps] = useState([]);
  const [clientDisabled, setClientDisabled] = useState({});
  const [stepClients, setStepClients] = useState([]);
  const [stepScope, setStepScope] = useState("");  // "" = all clients, else client_id
  const [savingSteps, setSavingSteps] = useState(false);
  const [loading, setLoading] = useState(true);
  const [onbEnabled, setOnbEnabled] = useState(false);
  const [onbCancelled, setOnbCancelled] = useState(false);
  const [reconciling, setReconciling] = useState(false);

  const load = useCallback(async (force = false) => {
    try {
      const [statusR, fundsR, stepsR] = await Promise.all([
        api.get(`/onboarding/cases/${caseId}/status${force ? "?refresh=true" : ""}`).then(r => r.data),
        api.get(`/onboarding/cases/${caseId}/funds`).then(r => r.data).catch(() => null),
        api.get(`/cases/${caseId}/onboarding-pack/steps`).then(r => r.data).catch(() => null),
      ]);
      setData(statusR);
      setFunds(fundsR);
      setDisabledSteps(stepsR?.disabled_steps || []);
      setClientDisabled(stepsR?.client_disabled_steps || {});
      setStepClients(stepsR?.clients || []);
      setOnbEnabled(!!stepsR?.onboarding_enabled);
      setOnbCancelled(!!stepsR?.onboarding_cancelled);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [caseId]);
  useEffect(() => { load(); }, [load]);

  // Is a given step currently removed for the selected scope?
  const isStepRemoved = useCallback((k) => {
    if (disabledSteps.includes(k)) return true;              // removed for everyone
    if (stepScope) return (clientDisabled[stepScope] || []).includes(k);
    return false;
  }, [disabledSteps, clientDisabled, stepScope]);

  // A step removed firm-wide is locked when editing a single client.
  const isStepLocked = useCallback((k) => !!stepScope && disabledSteps.includes(k), [stepScope, disabledSteps]);

  const toggleStep = useCallback(async (stepKey) => {
    if (isStepLocked(stepKey)) {
      toast.info(`"${STEP_LABELS[stepKey]}" is removed for all clients — change that under "All clients".`);
      return;
    }
    const removedNow = isStepRemoved(stepKey);
    setSavingSteps(true);
    try {
      if (stepScope) {
        const cur = clientDisabled[stepScope] || [];
        const next = removedNow ? cur.filter(s => s !== stepKey) : [...cur, stepKey];
        setClientDisabled({ ...clientDisabled, [stepScope]: next });  // optimistic
        await api.put(`/cases/${caseId}/onboarding-pack/steps`, { disabled_steps: next, client_id: stepScope });
      } else {
        const next = removedNow ? disabledSteps.filter(s => s !== stepKey) : [...disabledSteps, stepKey];
        setDisabledSteps(next);  // optimistic
        await api.put(`/cases/${caseId}/onboarding-pack/steps`, { disabled_steps: next });
      }
      const who = stepScope ? ` for ${(stepClients.find(c => c.id === stepScope) || {}).name || "this client"}` : "";
      toast.success(removedNow
        ? `Re-added "${STEP_LABELS[stepKey]}"${who}`
        : `Removed "${STEP_LABELS[stepKey]}"${who}`);
    } catch (e) {
      await load();  // revert to server truth
      toast.error(e.response?.data?.detail || "Could not update onboarding steps");
    } finally { setSavingSteps(false); }
  }, [caseId, stepScope, disabledSteps, clientDisabled, stepClients, isStepRemoved, isStepLocked, load]);

  // Per-client: is this step cancelled for one specific client?
  const isStepRemovedForClient = useCallback((k, clientId) => {
    if (disabledSteps.includes(k)) return true;               // cancelled for everyone
    return (clientDisabled[clientId] || []).includes(k);
  }, [disabledSteps, clientDisabled]);

  // Per-client cancel/restore — targets ONE client (used on each client row).
  const toggleStepForClient = useCallback(async (stepKey, clientId) => {
    if (!clientId) return;
    if (disabledSteps.includes(stepKey)) {
      toast.info(`"${STEP_LABELS[stepKey]}" is cancelled for all clients — restore it on the task header first.`);
      return;
    }
    const cur = clientDisabled[clientId] || [];
    const removedNow = cur.includes(stepKey);
    setSavingSteps(true);
    try {
      const next = removedNow ? cur.filter(s => s !== stepKey) : [...cur, stepKey];
      setClientDisabled({ ...clientDisabled, [clientId]: next });  // optimistic
      await api.put(`/cases/${caseId}/onboarding-pack/steps`, { disabled_steps: next, client_id: clientId });
      const name = (stepClients.find(c => c.id === clientId) || {}).name || "this client";
      toast.success(removedNow
        ? `Restored "${STEP_LABELS[stepKey]}" for ${name}`
        : `Cancelled "${STEP_LABELS[stepKey]}" for ${name}`);
    } catch (e) {
      await load();
      toast.error(e.response?.data?.detail || "Could not update onboarding steps");
    } finally { setSavingSteps(false); }
  }, [caseId, disabledSteps, clientDisabled, stepClients, load]);

  const hasCancellations = disabledSteps.length > 0 || Object.values(clientDisabled).some(v => (v || []).length > 0);
  const resetAllSteps = useCallback(async () => {
    setSavingSteps(true);
    try {
      await api.post(`/cases/${caseId}/onboarding-pack/steps/reset`);
      setDisabledSteps([]);        // optimistic
      setClientDisabled({});
      toast.success("All onboarding steps restored for every client");
    } catch (e) {
      await load();
      toast.error(e.response?.data?.detail || "Could not reset onboarding steps");
    } finally { setSavingSteps(false); }
  }, [caseId, load]);

  const cleanupQuestionnaires = useCallback(async () => {
    setSavingSteps(true);
    try {
      const r = await api.post(`/cases/${caseId}/onboarding-pack/questionnaires/cleanup`).then(res => res.data);
      const n = r?.removed || 0;
      toast.success(n ? `Removed ${n} form${n === 1 ? "" : "s"} that don't apply to this matter` : "No mismatched forms to remove");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not clean up forms");
    } finally { setSavingSteps(false); }
  }, [caseId, load]);

  const removeQuestionnaire = useCallback(async (key) => {
    setSavingSteps(true);
    try {
      const r = await api.post(`/cases/${caseId}/questionnaires/${key}/unassign`).then(res => res.data);
      toast.success(r?.removed ? `Removed the "${STEP_LABELS[key] || key}" form from this case` : "Nothing to remove — the form may be started or submitted");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not remove form");
    } finally { setSavingSteps(false); }
  }, [caseId, load]);

  const cancelOnboarding = useCallback(async () => {
    setSavingSteps(true);
    try {
      const r = await api.post(`/cases/${caseId}/onboarding-pack/cancel`).then(res => res.data);
      const n = r?.revoked_links || 0;
      const t = r?.removed_tasks || 0;
      toast.success(`Onboarding cancelled${n ? ` — ${n} magic link${n === 1 ? "" : "s"} revoked` : ""}${t ? `, ${t} form${t === 1 ? "" : "s"} removed` : ""}. Send a new link to start fresh.`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not cancel onboarding");
    } finally { setSavingSteps(false); }
  }, [caseId, load]);

  const nudge = useCallback(async ({ clientId, step, name }) => {
    try {
      const r = await api.post(`/onboarding/cases/${caseId}/nudge`, { client_id: clientId, step });
      toast.success(`Reminder emailed to ${r.data.emailed}`);
      await load();
    } catch (e) {
      if (e.response?.status === 429) {
        toast.error(e.response.data?.detail?.message || "Please wait before nudging again.");
      } else {
        toast.error(e.response?.data?.detail || `Could not nudge ${name || "client"}`);
      }
    }
  }, [caseId, load]);

  // Solicitor accept / cancel-&-re-request / clear for a step.
  const overrideStep = useCallback(async ({ clientId, step, action, caseLevel }) => {
    const body = { step, action };
    if (!caseLevel && clientId) body.client_id = clientId;
    try {
      const r = await api.post(`/onboarding/cases/${caseId}/step-override`, body);
      if (action === "accept") toast.success("Marked as done");
      else if (action === "reject") toast.success(r.data?.emailed ? `Cancelled — client asked to resubmit (${r.data.emailed})` : "Cancelled — marked for resubmission");
      else toast.success("Manual mark cleared");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update the step.");
    }
  }, [caseId, load]);


  const nudgeInfo = useCallback((step, clientId) => {
    const cooldown = data?.nudge_cooldown_seconds ?? 21600;
    const ts = data?.nudges?.[step]?.[clientId || "shared"];
    if (!ts) return { blocked: false, label: null };
    const elapsed = (Date.now() - new Date(ts).getTime()) / 1000;
    if (elapsed >= cooldown) return { blocked: false, label: _relative(ts) };
    return { blocked: true, label: `Last nudged ${_relative(ts)}` };
  }, [data]);

  // Bounce lookup — clients array carries hard-bounce flags.
  const bounceByEmail = useCallback((email) => {
    if (!email || !data?.clients) return null;
    const row = data.clients.find(c => (c.email || "").toLowerCase() === email.toLowerCase());
    return row?.email_bounced_at ? { at: row.email_bounced_at, reason: row.email_bounced_reason } : null;
  }, [data]);

  const clearBounce = useCallback(async (email) => {
    try {
      await api.post(`/onboarding/cases/${caseId}/clear-bounce`, { email });
      toast.success(`Bounce flag cleared for ${email}`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not clear the bounce flag");
    }
  }, [caseId, load]);

  // Clear Didit's false-positive name-split review straight from the
  // Onboarding tab — no need to open the Didit console. Only offered when
  // the full name genuinely reconciles (see backend detect_name_resplit_review).
  const approveNameReview = useCallback(async (sessionId) => {
    if (!sessionId) return;
    try {
      await api.post(`/didit/sessions/${sessionId}/approve-name-review`);
      toast.success("Name confirmed — ID check cleared");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't clear the name review");
    }
  }, [load]);

  // In-app review of an IN-REVIEW Didit check from the Onboarding tab.
  const [reviewBusy, setReviewBusy] = useState(false);
  const attachReport = useCallback(async (sessionId) => {
    if (!sessionId) return;
    setReviewBusy(true);
    const tid = toast.loading("Fetching report from Didit…");
    try {
      const { data: d } = await api.post(`/didit/sessions/${sessionId}/report`);
      toast.success("Report attached to the case", { id: tid });
      if (d?.report_doc_id) {
        await downloadAuthed(`/documents/${d.report_doc_id}/download`, "ID-verification-report.pdf").catch(() => {});
      }
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't fetch the report", { id: tid });
    } finally {
      setReviewBusy(false);
    }
  }, [load]);
  const reviewDecision = useCallback(async (sessionId, decision) => {
    if (!sessionId) return;
    if (decision === "Declined" && !window.confirm("Decline this ID check? The client will be marked as failed verification.")) return;
    setReviewBusy(true);
    const tid = toast.loading(decision === "Approved" ? "Approving with Didit…" : "Declining with Didit…");
    try {
      await api.post(`/didit/sessions/${sessionId}/approve-check`, { decision });
      toast.success(decision === "Approved" ? "Check approved — Didit & onboarding updated" : "Check declined", { id: tid });
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't update the check", { id: tid });
    } finally {
      setReviewBusy(false);
    }
  }, [load]);

  const reconcileIds = useCallback(async () => {
    if (!window.confirm(
      "Reconcile identity checks for this case?\n\nThis merges any duplicate Didit records per client — keeping the approved / most-complete one and archiving the rest — and re-syncs each client's wizard status. No new checks are started and nothing is billed."
    )) return;
    setReconciling(true);
    const tid = toast.loading("Reconciling identity records…");
    try {
      const { data: res } = await api.post(`/onboarding/cases/${caseId}/identity/reconcile`, {});
      toast.success(
        res.merged > 0
          ? `Reconciled ${res.merged} client record(s) — duplicates archived`
          : "Nothing to reconcile — records already clean",
        { id: tid });
      await load(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't reconcile identities", { id: tid });
    } finally {
      setReconciling(false);
    }
  }, [caseId, load]);

  if (!data) return <div className="p-6 text-sm text-red-500">Could not load onboarding status.</div>;

  // When onboarding has been cancelled, collapse the panel to just the
  // cancelled notice — the previous tasks/forms are gone and the wizard is
  // off, so we don't render the full step list. Sending a fresh Client Care
  // Pack (Authorities tab) re-activates it.
  if (onbCancelled) {
    return (
      <div className="space-y-6" data-testid="onboarding-panel-cancelled">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-slate-900">Client onboarding</h3>
            <p className="text-xs text-slate-500">Read-only view of the client wizard for this case.</p>
          </div>
          <button onClick={() => load(true)} className="text-xs text-slate-500 hover:text-slate-700 flex items-center gap-1" data-testid="onboarding-refresh-btn">
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </button>
        </div>
        <div className="rounded-lg border border-rose-200 bg-rose-50 p-5 flex items-start gap-3" data-testid="onboarding-cancelled-banner">
          <Ban className="w-6 h-6 text-rose-500 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-rose-800">Onboarding cancelled</p>
            <p className="text-xs text-rose-700 mt-1">All previous onboarding forms and tasks have been removed and the magic links revoked, so nothing is left on the client's wizard. To start fresh, send a new <b>Client Care Pack</b> from the Authorities tab — it re-activates the wizard with a new link and assigns only the forms that match this matter type.</p>
          </div>
        </div>
      </div>
    );
  }

  const scopeLabel = stepScope
    ? `just ${(stepClients.find(c => c.id === stepScope) || {}).name || "this client"}`
    : "all clients";
  // Shared props so every step shows a Cancel / Restore task button.
  const cancelProps = (k) => ({
    stepKey: k, removed: isStepRemoved(k), onCancel: toggleStep,
    saving: savingSteps, scopeLabel,
  });
  // Per-client cancel props for steps rendered as client rows.
  const clientCancel = {
    onCancelClient: toggleStepForClient,
    isRemovedForClient: isStepRemovedForClient,
    savingSteps,
  };
  // First table row index per client (so the questionnaires per-client
  // Cancel shows once per client, not once per form).
  const qFirstRowClient = {};
  (data.questionnaires || []).forEach((q, i) => {
    if (q.client_id && !(q.client_id in qFirstRowClient)) qFirstRowClient[q.client_id] = i;
  });
  // Detect type-specific forms from more than one matter family on the same
  // case (e.g. Sale + Purchase + Transfer) — always a stale-data mismatch.
  const TYPE_FAMILIES = ["purchase", "sale", "remortgage", "transfer"];
  const presentFamilies = [...new Set((data.questionnaires || []).map(q => q.key).filter(k => TYPE_FAMILIES.includes(k)))];
  const hasQMismatch = presentFamilies.length > 1;

  // Per-client completion state for the at-a-glance progress strip.
  // Returns "done" | "todo" | "cancelled" | "na" (not applicable / unassigned).
  const CASE_LEVEL_STEPS = ["payment", "funds"];
  const overrideFor = (cid, key) => {
    const bucket = CASE_LEVEL_STEPS.includes(key) ? "__case__" : cid;
    return (data?.overrides?.[bucket] || {})[key] || null;
  };

  const clientStepState = (cid, key) => {
    if (isStepRemovedForClient(key, cid)) return "cancelled";
    // Solicitor override wins over the real signal.
    const ov = overrideFor(cid, key);
    if (ov?.status === "accepted") return "done";
    if (ov?.status === "rejected") return "rejected";
    switch (key) {
      case "payment":
        return ["paid_card", "declared", "reconciled"].includes(data.payment?.status) ? "done" : "todo";
      case "identity": {
        const r = (data.identity || []).find(x => x.client_id === cid);
        return r ? (r.approved ? "done" : "todo") : "na";
      }
      case "ccl": {
        const r = (data.ccl?.signers || []).find(s => s.client_id === cid);
        return r ? (r.signed ? "done" : "todo") : "na";
      }
      case "questionnaires": {
        const qs = (data.questionnaires || []).filter(q => q.client_id === cid);
        if (!qs.length) return "na";
        return qs.every(q => q.status === "submitted") ? "done" : "todo";
      }
      case "ta": {
        const t = data.ta || {};
        const r = (t.signers || []).find(s => s.client_id === cid);
        if (r) return r.signed ? "done" : (r.stale ? "todo" : "todo");
        // Native TA is a case-level completion; if this client isn't matched
        // in the signer roll-up, fall back to the overall complete flag.
        if (t.native) return t.complete ? "done" : (t.signers?.length ? "todo" : "na");
        return "na";
      }
      case "funds":
        return funds?.required_met ? "done" : "todo";
      default:
        return "na";
    }
  };

  return (
    <div className="space-y-6" data-testid="onboarding-status-panel">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>Client onboarding</h3>
          <p className="text-xs text-slate-500">Read-only view of the client wizard for this case.</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => load(true)} className="text-xs text-slate-500 hover:text-slate-700 flex items-center gap-1" data-testid="onboarding-refresh-btn">
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </button>
          <button onClick={reconcileIds} disabled={reconciling}
            title="Merge duplicate ID records per client and re-sync wizard status (no new checks)"
            className="text-xs px-3 py-1.5 rounded-md border border-slate-300 text-slate-600 hover:bg-slate-700 hover:text-white disabled:opacity-50 transition-colors flex items-center gap-1"
            data-testid="reconcile-identities-btn">
            <RotateCcw className={`w-3.5 h-3.5 ${reconciling ? "animate-spin" : ""}`} /> Reconcile IDs
          </button>
          {onbEnabled && !onbCancelled && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <button type="button" disabled={savingSteps}
                  className="text-xs px-3 py-1.5 rounded-md border border-rose-300 text-rose-600 hover:bg-rose-600 hover:text-white disabled:opacity-50 transition-colors flex items-center gap-1"
                  data-testid="cancel-onboarding-btn">
                  <Ban className="w-3.5 h-3.5" /> Cancel onboarding
                </button>
              </AlertDialogTrigger>
              <AlertDialogContent data-testid="cancel-onboarding-dialog">
                <AlertDialogHeader>
                  <AlertDialogTitle>Cancel the whole onboarding & start fresh?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This stops the onboarding for this case and <b>revokes the magic links</b> already emailed to the client(s) — so a link sent to the wrong person stops working. It also <b>removes all onboarding forms</b> for this case (including any already started or submitted) so you start completely fresh. ID checks, payments and e-signatures already recorded are retained as case records. You can send a brand-new onboarding link afterwards.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel data-testid="cancel-onboarding-abort">Keep onboarding</AlertDialogCancel>
                  <AlertDialogAction onClick={cancelOnboarding} data-testid="cancel-onboarding-confirm"
                    className="bg-rose-600 hover:bg-rose-700">Cancel onboarding</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
          <button onClick={() => downloadAuthed(`/onboarding/cases/${caseId}/pack`, "onboarding-pack.zip").catch(() => toast.error("Could not download the pack."))}
             data-testid="onboarding-pack-btn"
             className="text-xs px-3 py-1.5 rounded-md bg-slate-900 text-white flex items-center gap-1 hover:bg-slate-700">
            <Package className="w-3.5 h-3.5" /> Download pack ZIP
          </button>
        </div>
      </div>

      {onbCancelled && (
        <div className="rounded-lg border border-rose-200 bg-rose-50 p-4 flex items-start gap-3" data-testid="onboarding-cancelled-banner">
          <Ban className="w-5 h-5 text-rose-500 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-rose-800">Onboarding cancelled</p>
            <p className="text-xs text-rose-700 mt-0.5">The previous magic links have been revoked. To start fresh, send a new onboarding link from the Client Care Pack — it will re-activate the wizard with a new link.</p>
          </div>
        </div>
      )}

      {/* At-a-glance per-client progress — who's finished which steps. */}
      {stepClients.length > 0 && (
        <div className="rounded-lg border border-slate-200 bg-white p-4" data-testid="onboarding-progress-strip">
          <p className="text-sm font-semibold text-slate-800 mb-3">Client progress</p>
          <div className="space-y-3">
            {stepClients.map((c) => {
              const states = STEP_ORDER.map(k => ({ key: k, state: clientStepState(c.id, k) }));
              const applicable = states.filter(s => s.state !== "cancelled" && s.state !== "na");
              const doneCount = applicable.filter(s => s.state === "done").length;
              const pct = applicable.length ? Math.round((doneCount / applicable.length) * 100) : 0;
              return (
                <div key={c.id} className="flex items-center gap-3 flex-wrap" data-testid={`progress-client-${c.id}`}>
                  <div className="min-w-[140px] max-w-[180px]">
                    <p className="text-sm font-medium text-slate-800 truncate">{c.name}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <div className="h-1.5 w-24 rounded-full bg-slate-100 overflow-hidden">
                        <div className={`h-full rounded-full transition-all ${pct === 100 ? "bg-emerald-500" : "bg-[#100f5d]"}`} style={{ width: `${pct}%` }} />
                      </div>
                      <span className="text-[11px] text-slate-500 shrink-0" data-testid={`progress-count-${c.id}`}>{doneCount}/{applicable.length}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 flex-wrap">
                    {states.map(({ key, state }) => {
                      if (state === "na") {
                        return (
                          <span key={key} title={`${STEP_LABELS[key]} — not applicable`}
                            className="text-[10px] font-semibold px-1.5 py-0.5 rounded border bg-slate-50 border-dashed border-slate-200 text-slate-300"
                            data-testid={`progress-pill-${c.id}-${key}`}>
                            {STEP_SHORT[key]}
                          </span>
                        );
                      }
                      const ov = overrideFor(c.id, key);
                      const caseLevel = CASE_LEVEL_STEPS.includes(key);
                      return (
                        <Popover key={key}>
                          <PopoverTrigger asChild>
                            <button
                              title={`${STEP_LABELS[key]} — ${state === "done" ? "completed" : state === "rejected" ? "rejected · awaiting resubmission" : state === "cancelled" ? "cancelled" : "outstanding"}${ov ? ` (manual: ${ov.status}${ov.by ? ` by ${ov.by}` : ""})` : ""}`}
                              className={`text-[10px] font-semibold px-1.5 py-0.5 rounded border transition-colors hover:ring-1 hover:ring-[#100f5d]/40 ${
                                state === "done" ? "bg-emerald-50 border-emerald-200 text-emerald-700" :
                                state === "rejected" ? "bg-rose-50 border-rose-200 text-rose-700" :
                                state === "todo" ? "bg-white border-slate-200 text-slate-500" :
                                state === "cancelled" ? "bg-slate-100 border-slate-200 text-slate-400 line-through" :
                                "bg-slate-50 border-dashed border-slate-200 text-slate-300"}`}
                              data-testid={`progress-pill-${c.id}-${key}`}>
                              {STEP_SHORT[key]}{ov ? "•" : ""}
                            </button>
                          </PopoverTrigger>
                          <PopoverContent align="start" className="w-56 p-2" data-testid={`override-menu-${c.id}-${key}`}>
                            <p className="text-xs font-semibold text-slate-700 px-1 pb-1.5">
                              {STEP_LABELS[key]}{caseLevel ? "" : ` — ${c.name}`}
                            </p>
                            {ov && (
                              <p className="text-[10px] text-slate-400 px-1 pb-1.5">
                                {_overrideVerb(ov.status)}{ov.by ? ` by ${ov.by}` : ""}{ov.at ? ` · ${_shortDate(ov.at)}` : ""}
                                {ov.note ? <span className="block italic mt-0.5">"{ov.note}"</span> : null}
                              </p>
                            )}
                            <div className="flex flex-col gap-1">
                              {state !== "done" && (
                                <button onClick={() => overrideStep({ clientId: c.id, step: key, action: "accept", caseLevel })}
                                  className="flex items-center gap-2 text-xs px-2 py-1.5 rounded hover:bg-emerald-50 text-emerald-700"
                                  data-testid={`override-accept-${c.id}-${key}`}>
                                  <Check className="w-3.5 h-3.5" /> Mark as done
                                </button>
                              )}
                              <button onClick={() => overrideStep({ clientId: c.id, step: key, action: "reject", caseLevel })}
                                className="flex items-center gap-2 text-xs px-2 py-1.5 rounded hover:bg-rose-50 text-rose-700"
                                data-testid={`override-reject-${c.id}-${key}`}>
                                <X className="w-3.5 h-3.5" /> Cancel &amp; re-request
                              </button>
                              {ov && (
                                <button onClick={() => overrideStep({ clientId: c.id, step: key, action: "clear", caseLevel })}
                                  className="flex items-center gap-2 text-xs px-2 py-1.5 rounded hover:bg-slate-100 text-slate-600"
                                  data-testid={`override-clear-${c.id}-${key}`}>
                                  <RotateCcw className="w-3.5 h-3.5" /> Clear manual mark
                                </button>
                              )}
                            </div>
                          </PopoverContent>
                        </Popover>
                      );
                    })}
                  </div>
                  {(() => {
                    const audits = STEP_ORDER
                      .map(k => ({ key: k, ov: overrideFor(c.id, k) }))
                      .filter(x => x.ov && (x.ov.status === "accepted" || x.ov.status === "rejected"));
                    if (!audits.length) return null;
                    return (
                      <div className="basis-full mt-1 space-y-0.5 pl-0.5" data-testid={`override-audit-list-${c.id}`}>
                        {audits.map(({ key, ov }) => (
                          <p key={key} className="text-[10px] text-slate-400" data-testid={`override-audit-${c.id}-${key}`}>
                            <span className={ov.status === "accepted" ? "font-semibold text-emerald-600" : "font-semibold text-rose-600"}>{STEP_LABELS[key]}</span>{" "}
                            {_overrideVerb(ov.status)}{ov.by ? ` by ${ov.by}` : ""}{ov.at ? ` · ${_shortDate(ov.at)}` : ""}
                            {ov.note ? <span className="italic"> — "{ov.note}"</span> : null}
                          </p>
                        ))}
                      </div>
                    );
                  })()}
                </div>
              );
            })}
          </div>
          <p className="text-[11px] text-slate-400 mt-3">Tap any tag to mark it done, or cancel &amp; re-request it. Green = completed · grey = outstanding · red = rejected (awaiting resubmission) · struck-through = cancelled · dashed = not applicable · • = set manually</p>
        </div>
      )}

      {/* Steps included — solicitor can remove tasks (e.g. skip ID check
          or questionnaires) even after the onboarding link has been sent. */}
      <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-4" data-testid="onboarding-steps-config">
        <div className="flex items-center justify-between mb-2 gap-3 flex-wrap">
          <div>
            <p className="text-sm font-semibold text-slate-800">Steps this client must complete</p>
            <p className="text-[11px] text-slate-500">Untick a step to remove it — takes effect immediately, including for links already sent.</p>
          </div>
          <div className="flex items-center gap-2">
            {savingSteps && <span className="text-[11px] text-slate-400">Saving…</span>}
            {hasCancellations && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <button type="button" disabled={savingSteps}
                    className="text-[11px] font-semibold px-2.5 py-1 rounded border border-[#100f5d] text-[#100f5d] hover:bg-[#100f5d] hover:text-white disabled:opacity-50 transition-colors flex items-center gap-1"
                    data-testid="onboarding-reset-all-steps">
                    <RotateCcw className="w-3.5 h-3.5" /> Reset all
                  </button>
                </AlertDialogTrigger>
                <AlertDialogContent data-testid="reset-all-confirm-dialog">
                  <AlertDialogHeader>
                    <AlertDialogTitle>Reset all onboarding steps?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This restores <b>every</b> step for <b>every</b> client on this case — including any tasks you deliberately cancelled for individual clients. This can’t be undone in one click.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel data-testid="reset-all-cancel">Keep current settings</AlertDialogCancel>
                    <AlertDialogAction onClick={resetAllSteps} data-testid="reset-all-confirm">Reset all steps</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
            <OnboardingStepPresets />
            {stepClients.length > 1 && (
              <select value={stepScope} onChange={e => setStepScope(e.target.value)}
                className="text-xs border border-slate-300 rounded-md px-2 py-1 bg-white"
                data-testid="onboarding-steps-scope">
                <option value="">All clients</option>
                {stepClients.map(c => <option key={c.id} value={c.id}>Just {c.name}</option>)}
              </select>
            )}
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {STEP_ORDER.map((k) => {
            const removed = isStepRemoved(k);
            const locked = isStepLocked(k);
            return (
              <label key={k}
                className={`flex items-center gap-2 px-2.5 py-2 rounded-md border text-xs transition-colors ${locked ? "cursor-not-allowed" : "cursor-pointer"} ${
                  removed ? "border-slate-200 bg-slate-100 text-slate-400 line-through" : "border-[#100f5d]/30 bg-white"
                }`}
                title={locked ? "Removed for all clients" : ""}
                data-testid={`onboarding-step-toggle-${k}`}>
                <input type="checkbox" checked={!removed} disabled={savingSteps || locked}
                  onChange={() => toggleStep(k)} className="w-4 h-4 shrink-0"
                  data-testid={`onboarding-step-check-${k}`} />
                <span className="truncate">{STEP_LABELS[k]}{locked ? " (all)" : ""}</span>
              </label>
            );
          })}
        </div>
        {stepScope && (
          <p className="text-[11px] text-slate-500 mt-2">Editing removals for <b>{(stepClients.find(c => c.id === stepScope) || {}).name}</b> only. Greyed "(all)" steps are removed for everyone.</p>
        )}
      </div>

      <CclLetterCard caseId={caseId} />

      {/* Step 1 · Payment */}
      <StepBox title="Step 1 · Payment on account" {...cancelProps("payment")} done={["paid_card", "declared", "reconciled"].includes(data.payment.status)}
        extra={!["paid_card", "reconciled"].includes(data.payment.status) && (() => {
          const info = nudgeInfo("payment", "shared");
          return <NudgeButton onClick={() => nudge({ clientId: null, step: "payment", name: "the client" })} testid="nudge-payment" blocked={info.blocked} subLabel={info.label} />;
        })()}>
        <div className="text-sm text-slate-700">
          Status: <PayStatusPill s={data.payment.status} />
          {data.payment.amount ? <span className="ml-2 text-slate-500">£{Number(data.payment.amount).toFixed(2)}</span> : null}
        </div>
        {data.payment.declaration && (
          <p className="text-xs text-slate-500 mt-1">
            Bank transfer declared by {data.payment.declaration.declared_by_name} on {new Date(data.payment.declaration.declared_at).toLocaleString("en-GB")}
            {data.payment.declaration.reconciled_at ? <> · reconciled {new Date(data.payment.declaration.reconciled_at).toLocaleDateString("en-GB")}</> : <> · <span className="text-amber-600 font-medium">awaiting reconciliation</span></>}
          </p>
        )}
        {data.payment.worldpay?.status === "paid" && (
          <p className="text-xs text-slate-500 mt-1">Card payment settled via WorldPay · tx <span className="font-mono">{data.payment.worldpay.transaction_reference}</span></p>
        )}
        {data.communication_consent?.consented && (
          <div className="mt-2 rounded border border-emerald-200 bg-emerald-50/60 p-2" data-testid="status-communication-consent">
            <p className="text-xs text-emerald-800">
              ✓ Communication / service-model consent acknowledged by {data.communication_consent.consented_by_name} on {new Date(data.communication_consent.consented_at).toLocaleString("en-GB")}.
            </p>
            <details className="mt-1">
              <summary className="text-[11px] text-emerald-700 cursor-pointer select-none">View the exact wording they ticked</summary>
              <p className="mt-1 text-[11px] leading-relaxed text-slate-600 italic">"{data.communication_consent.consent_text}"</p>
            </details>
          </div>
        )}
      </StepBox>

      {/* Step 2 · Identity (per client) */}
      <StepBox title="Step 2 · Identity" {...cancelProps("identity")} done={data.identity.length > 0 && data.identity.every(x => x.approved)}>
        <ClientGrid rows={data.identity.map(x => ({
          name: x.name, email: x.email, client_id: x.client_id,
          state: x.approved ? "done" : x.status === "Declined" ? "fail" : x.session_id ? "pending" : "todo",
          label: x.approved ? "Approved" : x.status || "Not started",
          extra: x.verified_at ? new Date(x.verified_at).toLocaleDateString("en-GB") : null,
          alerts: x.compliance_failures,
          name_review: x.name_review, name_review_override: x.name_review_override, session_id: x.session_id,
          status: x.status, report_doc_id: x.report_doc_id, approved: x.approved,
          reviewed_in_app_by: x.reviewed_in_app_by, reviewed_in_app_decision: x.reviewed_in_app_decision,
        }))} testid="onboarding-identity-grid" stepKey="identity" nudgeInfo={nudgeInfo}
        bounceByEmail={bounceByEmail} onClearBounce={clearBounce} {...clientCancel}
        onApproveNameReview={approveNameReview}
        onReviewDecision={reviewDecision} onAttachReport={attachReport} reviewBusy={reviewBusy}
        onNudge={(row) => nudge({ clientId: row.client_id, step: "identity", name: row.name })} />
      </StepBox>

      {/* Step 3 · CCL */}
      <StepBox title="Step 3 · Client Care Letter" {...cancelProps("ccl")} done={data.ccl.all_signed}
        extra={data.ccl.document_id && <DocLink id={data.ccl.document_id} label="Signed CCL" />}>
        <ClientGrid rows={data.ccl.signers.map(s => ({
          name: s.name, client_id: s.client_id, email: _emailForClient(data, s.client_id),
          state: s.signed ? "done" : "todo",
          label: s.signed ? "Signed" : "Awaiting signature",
          extra: s.signed_at ? new Date(s.signed_at).toLocaleString("en-GB") : null,
        }))} testid="onboarding-ccl-grid" stepKey="ccl" nudgeInfo={nudgeInfo}
        bounceByEmail={bounceByEmail} onClearBounce={clearBounce} {...clientCancel}
        onNudge={(row) => nudge({ clientId: row.client_id, step: "ccl", name: row.name })} />
      </StepBox>

      {/* Step 4 · Questionnaires (per client × key grid) */}
      <StepBox title="Step 4 · Client questionnaires" {...cancelProps("questionnaires")} done={data.questionnaires.length > 0 && data.questionnaires.every(q => q.status === "submitted")}
        extra={<AssignAllApplicableButton caseId={caseId} onDone={load} />}>
        {hasQMismatch && (
          <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 p-3 flex items-start gap-2" data-testid="qs-mismatch-notice">
            <AlertCircle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-amber-800">Forms from more than one matter type are assigned</p>
              <p className="text-[11px] text-amber-700 mt-0.5">This case has {presentFamilies.map(f => STEP_LABELS[f] ? f : f).join(", ")} questionnaires — usually a leftover from a matter-type change. Remove the ones that don't apply (completed forms are kept).</p>
            </div>
            <button type="button" onClick={cleanupQuestionnaires} disabled={savingSteps}
              className="text-[11px] font-semibold px-2.5 py-1 rounded border border-amber-400 text-amber-800 hover:bg-amber-600 hover:text-white disabled:opacity-50 transition-colors shrink-0"
              data-testid="qs-cleanup-btn">
              Remove forms that don't apply
            </button>
          </div>
        )}
        <div className="overflow-x-auto -mx-6 sm:mx-0" data-testid="onboarding-qs-grid">
          <table className="min-w-full text-sm">
            <thead className="text-xs text-slate-500 uppercase tracking-wider">
              <tr><th className="text-left px-3 py-2">Client</th><th className="text-left px-3 py-2">Form</th><th className="text-left px-3 py-2">Status</th><th className="text-left px-3 py-2">Updated</th><th className="text-right px-3 py-2"></th></tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data.questionnaires.map((q, i) => {
                const qRemoved = isStepRemovedForClient("questionnaires", q.client_id);
                return (
                <tr key={i} className={qRemoved ? "bg-slate-100/60" : ""}>
                  <td className={`px-3 py-2 ${qRemoved ? "text-slate-400 line-through" : "text-slate-800"}`}>{q.name}</td>
                  <td className={`px-3 py-2 ${qRemoved ? "text-slate-400" : "text-slate-600"}`}>{q.key}</td>
                  <td className="px-3 py-2">{qRemoved ? <span className="text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded bg-slate-200 text-slate-500">Cancelled</span> : <QStatusPill s={q.status} />}</td>
                  <td className="px-3 py-2 text-xs text-slate-500">{q.submitted_at ? new Date(q.submitted_at).toLocaleDateString("en-GB") : q.last_saved_at ? new Date(q.last_saved_at).toLocaleDateString("en-GB") : "—"}</td>
                  <td className="px-3 py-2 text-right">
                    <div className="inline-flex items-center gap-2 justify-end">
                      {!qRemoved && q.status !== "submitted" && q.status !== "not_assigned" && (() => {
                        const info = nudgeInfo("questionnaires", q.client_id);
                        return <NudgeButton small onClick={() => nudge({ clientId: q.client_id, step: "questionnaires", name: q.name })} testid={`nudge-q-${i}`} blocked={info.blocked} subLabel={info.label} />;
                      })()}
                      {q.client_id && qFirstRowClient[q.client_id] === i && !disabledSteps.includes("questionnaires") && (
                        <button type="button" onClick={() => toggleStepForClient("questionnaires", q.client_id)} disabled={savingSteps}
                          title={qRemoved ? "Restore questionnaires for this client" : "Cancel questionnaires for this client only"}
                          className={`inline-flex items-center gap-1 rounded font-medium border transition-colors text-[10px] px-2 py-1 disabled:opacity-50 ${
                            qRemoved ? "border-[#100f5d] text-[#100f5d] hover:bg-[#100f5d] hover:text-white"
                                     : "border-rose-300 text-rose-600 hover:bg-rose-600 hover:text-white"}`}
                          data-testid={`cancel-client-questionnaires-${i}`}>
                          {qRemoved ? <><RotateCcw className="w-3 h-3" /> Restore</> : <><Ban className="w-3 h-3" /> Cancel</>}
                        </button>
                      )}
                      {!qRemoved && q.status === "assigned" && (
                        <button type="button" onClick={() => removeQuestionnaire(q.key)} disabled={savingSteps}
                          title={`Remove the "${q.key}" form from this case (all clients). Only unstarted forms can be removed.`}
                          className="inline-flex items-center gap-1 rounded font-medium border border-rose-300 text-rose-600 hover:bg-rose-600 hover:text-white disabled:opacity-50 transition-colors text-[10px] px-2 py-1"
                          data-testid={`remove-q-${i}`}>
                          <Trash2 className="w-3 h-3" /> Remove
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );})}
              {data.questionnaires.length === 0 && (
                <tr><td colSpan={5} className="px-3 py-6 text-xs text-center">
                  <p className="text-slate-500">Nothing assigned yet.</p>
                  <p className="text-slate-400 mt-1">
                    Use the <b>Assign onboarding questionnaires</b> button (top right)
                    to auto-send the ones that apply to this matter.
                  </p>
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </StepBox>

      {/* Step 5 · TA form */}
      <StepBox title="Step 5 · Property Information form" {...cancelProps("ta")} done={data.ta.complete ?? (data.ta.signers.length > 0 && data.ta.signers.every(s => s.signed))}
        extra={data.ta.document_id && <DocLink id={data.ta.document_id} label="Signed TA" />}>
        {data.ta.has_answers ? (
          <p className="text-xs text-slate-500 mb-2">
            First filled by <strong>{Object.values(data.ta.filled_by || {})[0]?.name || "—"}</strong>
            {data.ta.answers_updated_at ? <> · last edited {new Date(data.ta.answers_updated_at).toLocaleString("en-GB")}</> : null}
          </p>
        ) : <p className="text-xs text-slate-500 mb-2">Not started yet.</p>}
        <ClientGrid rows={data.ta.signers.map(s => ({
          name: s.name, client_id: s.client_id, email: _emailForClient(data, s.client_id),
          state: s.signed ? "done" : s.stale ? "warn" : "todo",
          label: s.signed ? "Signed & verified" : s.stale ? "Stale — needs re-verify" : "Awaiting",
          extra: s.signed_at ? new Date(s.signed_at).toLocaleString("en-GB") : null,
        }))} testid="onboarding-ta-grid" stepKey="ta" nudgeInfo={nudgeInfo}
        bounceByEmail={bounceByEmail} onClearBounce={clearBounce} {...clientCancel}
        onNudge={(row) => nudge({ clientId: row.client_id, step: "ta", name: row.name })} />
        <TaNativeSolicitorForms caseId={caseId} />
      </StepBox>

      {/* Proof of funds (client uploads) — last step */}
      <FundsBox funds={funds} {...cancelProps("funds")} />

      {/* Gifted deposits — giftors (declaration + funds + ID/AML) */}
      <GiftorsBox caseId={caseId} />
    </div>
  );
}

function GiftorsBox({ caseId }) {
  const [giftors, setGiftors] = useState(null);
  const load = useCallback(async () => {
    try { const { data } = await api.get(`/onboarding/cases/${caseId}/giftors`); setGiftors(data.giftors || []); }
    catch { setGiftors([]); }
  }, [caseId]);
  useEffect(() => { load(); }, [load]);
  const list = giftors || [];
  const allDone = list.length > 0 && list.every(g => g.status === "complete");
  return (
    <StepBox title="Gifted deposits" done={allDone} extra={
      list.length > 0 ? (
        <span className={`text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded ${allDone ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
          {allDone ? "All complete" : `${list.filter(g => g.status === "complete").length}/${list.length} complete`}
        </span>
      ) : null
    }>
      <div data-testid="onboarding-giftors-box">
        {giftors === null
          ? <p className="text-xs text-slate-400">Loading…</p>
          : <GiftorsManager caseId={caseId} role="solicitor" giftors={list} refresh={load} />}
      </div>
    </StepBox>
  );
}

// ─── little helpers ──────────────────────────────────────────────────
function FundsBox({ funds, ...cancel }) {
  const proof = funds?.proof_of_funds || [];
  const mortgage = funds?.mortgage_offer || [];
  const giftorFunds = funds?.giftor_funds || [];
  const done = !!funds?.required_met;
  const FileRow = ({ f }) => (
    <button type="button"
      onClick={() => downloadAuthed(`/documents/${f.id}/download`, f.filename).catch(() => toast.error("Could not download this file."))}
      className="w-full flex items-center gap-2 rounded border border-slate-200 bg-white px-3 py-2 text-left hover:bg-slate-50"
      data-testid={`onboarding-funds-file-${f.id}`}>
      <FileText className="w-4 h-4 text-[#100f5d] shrink-0" />
      <span className="text-sm text-slate-800 truncate flex-1">{f.filename}</span>
      <span className="text-[11px] text-slate-400 shrink-0">{f.uploaded_at ? new Date(f.uploaded_at).toLocaleString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : ""}</span>
    </button>
  );
  return (
    <StepBox title="Step 6 · Proof of funds" done={done} {...cancel} extra={
      <span className={`text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded ${done ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
        {done ? "Received" : "Outstanding"}
      </span>
    }>
      <div className="space-y-3" data-testid="onboarding-funds-box">
        <div>
          <p className="text-xs font-semibold text-slate-600 mb-1.5">Proof of funds <span className="text-slate-400 font-normal">(bank statements, savings, gift letters)</span></p>
          {proof.length ? (
            <div className="space-y-1.5" data-testid="onboarding-funds-proof-list">{proof.map(f => <FileRow key={f.id} f={f} />)}</div>
          ) : <p className="text-xs text-slate-400">No files uploaded yet.</p>}
        </div>
        <div>
          <p className="text-xs font-semibold text-slate-600 mb-1.5">Mortgage offer <span className="text-slate-400 font-normal">(optional — cash buyers have none)</span></p>
          {mortgage.length ? (
            <div className="space-y-1.5" data-testid="onboarding-funds-mortgage-list">{mortgage.map(f => <FileRow key={f.id} f={f} />)}</div>
          ) : <p className="text-xs text-slate-400">No mortgage offer uploaded.</p>}
        </div>
        {giftorFunds.length > 0 && (
          <div>
            <p className="text-xs font-semibold text-slate-600 mb-1.5">Gifted deposit — proof of funds <span className="text-slate-400 font-normal">(uploaded by giftors · staff only)</span></p>
            <div className="space-y-1.5" data-testid="onboarding-funds-giftor-list">
              {giftorFunds.map(f => (
                <button key={f.id} type="button"
                  onClick={() => downloadAuthed(`/documents/${f.id}/download`, f.filename).catch(() => toast.error("Could not download this file."))}
                  className="w-full flex items-center gap-2 rounded border border-slate-200 bg-white px-3 py-2 text-left hover:bg-slate-50"
                  data-testid={`onboarding-giftor-funds-file-${f.id}`}>
                  <Gift className="w-4 h-4 text-[#100f5d] shrink-0" />
                  <span className="text-sm text-slate-800 truncate flex-1">{f.filename}</span>
                  {f.giftor_name ? <span className="text-[11px] text-slate-500 shrink-0">{f.giftor_name}</span> : null}
                  <span className="text-[11px] text-slate-400 shrink-0">{f.uploaded_at ? new Date(f.uploaded_at).toLocaleString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : ""}</span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </StepBox>
  );
}

// Solicitor controls for the official-form TA6/TA7/TA10 (native flow):
// download the signed PDF, regenerate it with the latest overlay, or
// unlock it so the sellers can amend + re-sign.
function TaNativeSolicitorForms({ caseId }) {
  const [forms, setForms] = useState(null);
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/onboarding/cases/${caseId}/ta-native/status`);
      setForms(data?.enabled ? (data.forms || []) : []);
    } catch { setForms([]); }
  }, [caseId]);
  useEffect(() => { load(); }, [load]);

  const regenerate = async (form) => {
    setBusy(`regen-${form}`);
    try {
      await api.post(`/onboarding/cases/${caseId}/ta-native/${form}/regenerate`, {});
      toast.success(`${form} regenerated onto the official form.`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Could not regenerate ${form}.`);
    } finally { setBusy(""); }
  };
  const unlock = async (form) => {
    setBusy(`unlock-${form}`);
    try {
      await api.post(`/onboarding/cases/${caseId}/ta-native/${form}/unlock`, {});
      toast.success(`${form} unlocked — sellers can amend and re-sign.`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Could not unlock ${form}.`);
    } finally { setBusy(""); }
  };

  if (!forms || forms.length === 0) return null;
  return (
    <div className="mt-4 rounded-lg border border-slate-200 divide-y" data-testid="ta-native-solicitor-forms">
      {forms.map((f) => {
        const signed = f.all_signed && f.final_document_id;
        return (
          <div key={f.form} className="p-3 flex items-center justify-between gap-3 flex-wrap"
            data-testid={`ta-native-row-${f.form}`}>
            <div className="flex items-center gap-2 min-w-0">
              {signed ? <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                : <CircleDashed className="w-4 h-4 text-slate-300 shrink-0" />}
              <span className="text-sm font-medium text-slate-800 truncate">{f.label || f.form}</span>
              <span className="text-[11px] text-slate-500 shrink-0">
                {signed ? `Signed (${f.signed_count}/${f.signer_count})`
                  : f.answers_started ? `In progress (${f.signed_count}/${f.signer_count} signed)`
                  : "Not started"}
              </span>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {f.final_document_id && (
                <button type="button"
                  onClick={() => downloadAuthed(`/documents/${f.final_document_id}/download`, `${f.form}.pdf`)
                    .catch(() => toast.error("Could not download the document."))}
                  className="text-[11px] font-semibold px-2.5 py-1 rounded border border-[#100f5d] text-[#100f5d] hover:bg-[#100f5d] hover:text-white transition-colors flex items-center gap-1"
                  data-testid={`ta-native-download-${f.form}`}>
                  <FileText className="w-3.5 h-3.5" /> Download
                </button>
              )}
              {signed && (
                <button type="button" disabled={!!busy} onClick={() => regenerate(f.form)}
                  className="text-[11px] font-semibold px-2.5 py-1 rounded border border-slate-300 text-slate-700 hover:bg-slate-700 hover:text-white disabled:opacity-50 transition-colors flex items-center gap-1"
                  title="Re-render the signed PDF onto the latest official form (no re-signing needed)"
                  data-testid={`ta-native-regenerate-${f.form}`}>
                  <RefreshCw className={`w-3.5 h-3.5 ${busy === `regen-${f.form}` ? "animate-spin" : ""}`} /> Regenerate PDF
                </button>
              )}
              {signed && (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <button type="button" disabled={!!busy}
                      className="text-[11px] font-semibold px-2.5 py-1 rounded border border-amber-300 text-amber-700 hover:bg-amber-600 hover:text-white disabled:opacity-50 transition-colors flex items-center gap-1"
                      data-testid={`ta-native-unlock-${f.form}`}>
                      <RotateCcw className="w-3.5 h-3.5" /> Unlock to edit
                    </button>
                  </AlertDialogTrigger>
                  <AlertDialogContent data-testid={`ta-native-unlock-dialog-${f.form}`}>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Unlock {f.form} for editing?</AlertDialogTitle>
                      <AlertDialogDescription>
                        This voids the {f.signer_count} signature{f.signer_count === 1 ? "" : "s"} on {f.form}.
                        The sellers can then amend their answers via their onboarding link and re-sign — a fresh
                        PDF is generated on the official form when everyone has signed again. The current signed
                        PDF stays on file in the case documents.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel data-testid={`ta-native-unlock-cancel-${f.form}`}>Keep signed</AlertDialogCancel>
                      <AlertDialogAction onClick={() => unlock(f.form)}
                        data-testid={`ta-native-unlock-confirm-${f.form}`}>Unlock</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function StepBox({ title, done, children, extra, stepKey, removed, onCancel, saving, scopeLabel }) {
  return (
    <section className={`rounded-lg border p-5 ${removed ? "border-slate-200 bg-slate-100/70" : done ? "border-emerald-200 bg-emerald-50/40" : "border-slate-200 bg-white"}`}
      data-testid={stepKey ? `onboarding-stepbox-${stepKey}` : undefined}>
      <header className="flex items-center justify-between mb-3 gap-2">
        <div className="flex items-center gap-2 min-w-0">
          {removed ? <Ban className="w-5 h-5 text-slate-400 shrink-0" />
            : done ? <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />
            : <CircleDashed className="w-5 h-5 text-slate-300 shrink-0" />}
          <h4 className={`text-sm font-semibold truncate ${removed ? "text-slate-400 line-through" : "text-slate-900"}`}>{title}</h4>
          {removed && (
            <span className="text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded bg-slate-200 text-slate-500 shrink-0">Cancelled</span>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {!removed && extra}
          {stepKey && onCancel && (
            removed ? (
              <button type="button" onClick={() => onCancel(stepKey)} disabled={saving}
                className="text-[11px] font-semibold px-2.5 py-1 rounded border border-[#100f5d] text-[#100f5d] hover:bg-[#100f5d] hover:text-white disabled:opacity-50 transition-colors flex items-center gap-1"
                data-testid={`onboarding-restore-step-${stepKey}`}>
                <RotateCcw className="w-3.5 h-3.5" /> Restore task
              </button>
            ) : (
              <button type="button" onClick={() => onCancel(stepKey)} disabled={saving}
                className="text-[11px] font-semibold px-2.5 py-1 rounded border border-rose-300 text-rose-600 hover:bg-rose-600 hover:text-white disabled:opacity-50 transition-colors flex items-center gap-1"
                data-testid={`onboarding-cancel-step-${stepKey}`}>
                <Ban className="w-3.5 h-3.5" /> Cancel task
              </button>
            )
          )}
        </div>
      </header>
      {removed ? (
        <p className="text-xs text-slate-400" data-testid={`onboarding-step-cancelled-note-${stepKey}`}>
          This task is cancelled{scopeLabel ? ` for ${scopeLabel}` : ""} — the client no longer sees it in their onboarding. Click <b>Restore task</b> to add it back.
        </p>
      ) : children}
    </section>
  );
}

// ─── Assign onboarding questionnaires (one-click backfill) ──────────
// Calls POST /api/cases/:id/questionnaires/assign-all-applicable which
// idempotently inserts a `case_questionnaires` row for every
// questionnaire that applies to this matter (source_of_funds +
// personal_info + purchase/sale/remortgage/transfer where relevant).
// Purpose-built for cases whose onboarding pack was generated BEFORE
// the auto-assign hook shipped — the solicitor can click this once
// and the client's wizard starts showing the questionnaires.
function AssignAllApplicableButton({ caseId, onDone }) {
  const [busy, setBusy] = useState(false);
  const click = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/cases/${caseId}/questionnaires/assign-all-applicable`);
      const n = r.data?.assigned ?? 0;
      const keys = (r.data?.keys || []).join(", ");
      toast.success(
        n === 0
          ? `All applicable questionnaires already assigned (${keys}).`
          : `Assigned ${n} new questionnaire row(s) (${keys}).`,
      );
      if (onDone) await onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not assign questionnaires.");
    } finally { setBusy(false); }
  };
  return (
    <button type="button" onClick={click} disabled={busy}
      className="text-[11px] font-semibold px-2.5 py-1 rounded border border-[#100f5d] text-[#100f5d] hover:bg-[#100f5d] hover:text-white disabled:opacity-50 transition-colors"
      data-testid="onboarding-qs-assign-all">
      {busy ? "Assigning…" : "Assign onboarding questionnaires"}
    </button>
  );
}


function ClientGrid({ rows, testid, onNudge, nudgeInfo, stepKey, bounceByEmail, onClearBounce, onCancelClient, isRemovedForClient, savingSteps, onApproveNameReview, onReviewDecision, onAttachReport, reviewBusy }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-2" data-testid={testid}>
      {rows.map((r, i) => {
        const info = onNudge && stepKey && nudgeInfo ? nudgeInfo(stepKey, r.client_id) : { blocked: false, label: null };
        const bounce = bounceByEmail ? bounceByEmail(r.email) : null;
        const removedForClient = isRemovedForClient && stepKey && r.client_id ? isRemovedForClient(stepKey, r.client_id) : false;
        const nameReviewClearable = !!onApproveNameReview && r.name_review?.is_candidate && !r.name_review_override && r.state !== "done" && !removedForClient;
        const inReview = stepKey === "identity" && !!onReviewDecision && !r.approved && !removedForClient
          && !!r.session_id && ["in review", "in_review"].includes((r.status || "").toLowerCase());
        // Passed (approved) and failed (declined) checks also have a Didit
        // report — let the solicitor pull/download it here, not just In Review.
        const _idStatus = (r.status || "").toLowerCase();
        const terminalReport = stepKey === "identity" && !!r.session_id && !removedForClient && !inReview
          && (r.approved || ["approved", "declined", "failed", "rejected"].includes(_idStatus));
        return (
        <div key={i} className={`rounded border text-sm ${
          removedForClient      ? "border-slate-200 bg-slate-100/70" :
          bounce                ? "border-red-300 bg-red-50/60" :
          r.state === "done"    ? "border-emerald-200 bg-white" :
          r.state === "fail"    ? "border-red-200 bg-red-50/50" :
          r.state === "warn"    ? "border-amber-200 bg-amber-50/50" :
          r.state === "pending" ? "border-blue-200 bg-blue-50/50" :
                                  "border-slate-200 bg-white"}`}>
          <div className="flex items-center justify-between gap-2 px-3 py-2">
          <div className="min-w-0">
            <p className={`font-medium truncate ${removedForClient ? "text-slate-400 line-through" : "text-slate-800"}`}>{r.name}</p>
            {r.email && <p className="text-[11px] text-slate-500 truncate">{r.email}</p>}
            {removedForClient && (
              <p className="text-[11px] text-slate-400 mt-0.5">Task cancelled for this client</p>
            )}
            {bounce && !removedForClient && (
              <p className="text-[11px] text-red-700 flex items-center gap-1 mt-0.5" data-testid={`bounce-${testid}-${i}`}>
                <MailX className="w-3 h-3 shrink-0" />
                <span className="truncate">Email hard-bounced {_relative(bounce.at)} — call the client</span>
                {onClearBounce && (
                  <button onClick={() => onClearBounce(r.email)} className="ml-1 underline hover:no-underline font-medium" data-testid={`clear-bounce-${testid}-${i}`}>clear</button>
                )}
              </p>
            )}
            {!removedForClient && (r.alerts || []).slice(0, 2).map((a, j) => (
              <p key={j} className="text-[11px] text-red-600 truncate flex items-center gap-1"><AlertCircle className="w-3 h-3 shrink-0" />{a}</p>
            ))}
          </div>
          <div className="text-right shrink-0 flex items-center gap-2">
            {!removedForClient && (
              <div>
                <span className={`text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded ${
                  r.state === "done" ? "bg-emerald-100 text-emerald-700" :
                  r.state === "fail" ? "bg-red-100 text-red-700" :
                  r.state === "warn" ? "bg-amber-100 text-amber-700" :
                  r.state === "pending" ? "bg-blue-100 text-blue-700" :
                  "bg-slate-100 text-slate-500"}`}>{r.label}</span>
                {r.extra && <p className="text-[10px] text-slate-400 mt-0.5">{r.extra}</p>}
              </div>
            )}
            {onNudge && r.state !== "done" && !removedForClient && (
              <NudgeButton small onClick={() => onNudge(r)} testid={`nudge-${testid}-${i}`}
                blocked={info.blocked || !!bounce} subLabel={bounce ? "Bouncing" : info.label} />
            )}
            {onCancelClient && stepKey && r.client_id && (
              <button type="button" onClick={() => onCancelClient(stepKey, r.client_id)} disabled={savingSteps}
                title={removedForClient ? "Restore this task for this client" : "Cancel this task for this client only"}
                className={`inline-flex items-center gap-1 rounded font-medium border transition-colors text-[10px] px-2 py-1 disabled:opacity-50 ${
                  removedForClient
                    ? "border-[#100f5d] text-[#100f5d] hover:bg-[#100f5d] hover:text-white"
                    : "border-rose-300 text-rose-600 hover:bg-rose-600 hover:text-white"}`}
                data-testid={`cancel-client-${stepKey}-${i}`}>
                {removedForClient ? <><RotateCcw className="w-3 h-3" /> Restore</> : <><Ban className="w-3 h-3" /> Cancel</>}
              </button>
            )}
          </div>
          </div>
          {nameReviewClearable && (
            <div className="px-3 pb-2.5 -mt-0.5" data-testid={`onboarding-name-review-${r.client_id}`}>
              <div className="rounded-md bg-indigo-50 border border-indigo-200 p-2 text-[11px] text-indigo-900">
                <p className="leading-snug">
                  Didit flagged a name mismatch. Passport reads <strong>{r.name_review.extracted_given} {r.name_review.extracted_surname || ""}</strong>; the client split it into First <strong>{r.name_review.confirmed_first || "—"}</strong>{r.name_review.confirmed_middle ? <> / Middle <strong>{r.name_review.confirmed_middle}</strong></> : null}. The full name matches.
                </p>
                <button type="button" onClick={() => onApproveNameReview(r.session_id)}
                  className="mt-2 inline-flex items-center gap-1 rounded font-semibold text-[11px] px-2.5 py-1 bg-indigo-600 text-white hover:bg-indigo-700 transition-colors"
                  data-testid={`onboarding-approve-name-review-${r.client_id}`}>
                  <CheckCircle2 className="w-3.5 h-3.5" /> Confirm name & clear ID check
                </button>
              </div>
            </div>
          )}
          {inReview && (
            <div className="px-3 pb-2.5 -mt-0.5" data-testid={`onboarding-review-panel-${r.client_id}`}>
              <div className="rounded-md bg-amber-50 border border-amber-200 p-2 text-[11px] text-amber-900">
                <p className="leading-snug font-medium">This ID check is in review. Read the report and decide here — no need to open Didit.</p>
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {r.report_doc_id ? (
                    <button type="button" onClick={() => downloadAuthed(`/documents/${r.report_doc_id}/download`, "ID-verification-report.pdf").catch(() => toast.error("Could not download the report."))}
                      className="inline-flex items-center gap-1 rounded font-medium text-[11px] px-2.5 py-1 border border-amber-300 text-amber-800 hover:bg-amber-100 transition-colors"
                      data-testid={`onboarding-view-report-${r.client_id}`}>
                      <FileText className="w-3.5 h-3.5" /> Download report
                    </button>
                  ) : (
                    <button type="button" onClick={() => onAttachReport(r.session_id)} disabled={reviewBusy}
                      className="inline-flex items-center gap-1 rounded font-medium text-[11px] px-2.5 py-1 border border-amber-300 text-amber-800 hover:bg-amber-100 transition-colors disabled:opacity-50"
                      data-testid={`onboarding-attach-report-${r.client_id}`}>
                      <FileText className="w-3.5 h-3.5" /> View report
                    </button>
                  )}
                  <button type="button" onClick={() => onReviewDecision(r.session_id, "Approved")} disabled={reviewBusy}
                    className="inline-flex items-center gap-1 rounded font-semibold text-[11px] px-2.5 py-1 bg-emerald-600 text-white hover:bg-emerald-700 transition-colors disabled:opacity-50"
                    data-testid={`onboarding-approve-check-${r.client_id}`}>
                    <CheckCircle2 className="w-3.5 h-3.5" /> Approve check
                  </button>
                  <button type="button" onClick={() => onReviewDecision(r.session_id, "Declined")} disabled={reviewBusy}
                    className="inline-flex items-center gap-1 rounded font-medium text-[11px] px-2.5 py-1 border border-rose-300 text-rose-700 hover:bg-rose-50 transition-colors disabled:opacity-50"
                    data-testid={`onboarding-decline-check-${r.client_id}`}>
                    Decline
                  </button>
                </div>
              </div>
            </div>
          )}
          {terminalReport && (
            <div className="px-3 pb-2.5 -mt-0.5" data-testid={`onboarding-report-row-${r.client_id}`}>
              {r.report_doc_id ? (
                <button type="button" onClick={() => downloadAuthed(`/documents/${r.report_doc_id}/download`, "ID-verification-report.pdf").catch(() => toast.error("Could not download the report."))}
                  className="inline-flex items-center gap-1 rounded font-medium text-[11px] px-2.5 py-1 border border-slate-300 text-slate-700 hover:bg-slate-100 transition-colors"
                  data-testid={`onboarding-report-download-${r.client_id}`}>
                  <FileText className="w-3.5 h-3.5" /> Download ID report
                </button>
              ) : (
                <button type="button" onClick={() => onAttachReport(r.session_id)} disabled={reviewBusy}
                  className="inline-flex items-center gap-1 rounded font-medium text-[11px] px-2.5 py-1 border border-slate-300 text-slate-700 hover:bg-slate-100 transition-colors disabled:opacity-50"
                  data-testid={`onboarding-report-get-${r.client_id}`}>
                  <FileText className="w-3.5 h-3.5" /> {reviewBusy ? "Fetching…" : "Get ID report from Didit"}
                </button>
              )}
            </div>
          )}
          {r.reviewed_in_app_by && (
            <div className="px-3 pb-2.5 -mt-0.5" data-testid={`onboarding-reviewed-by-${r.client_id}`}>
              <p className="text-[10px] text-slate-500">Reviewed in ConveyHub by {r.reviewed_in_app_by} ({r.reviewed_in_app_decision}).</p>
            </div>
          )}
        </div>
      );})}
    </div>
  );
}
function NudgeButton({ onClick, testid, small, blocked, subLabel }) {
  return (
    <span className="inline-flex flex-col items-end gap-0.5">
      <button type="button" onClick={onClick} data-testid={testid} disabled={blocked}
        title={blocked ? subLabel : "Send a reminder email"}
        className={`inline-flex items-center gap-1 rounded font-medium border transition-colors
          ${small ? "text-[10px] px-2 py-1 border-slate-200 bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white" : "text-xs px-3 py-1.5 border-[#100f5d] text-[#100f5d] hover:bg-[#100f5d] hover:text-white disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:text-[#100f5d]"}`}>
        <Bell className="w-3 h-3" /> Nudge
      </button>
      {subLabel && <span className="text-[9px] text-slate-400">{subLabel}</span>}
    </span>
  );
}

function _relative(iso) {
  const then = new Date(iso).getTime();
  const s = Math.max(1, Math.floor((Date.now() - then) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}
function _emailForClient(data, clientId) {
  return (data?.clients || []).find(c => c.client_id === clientId)?.email || null;
}
// Compliance audit helpers for solicitor step overrides.
function _overrideVerb(status) {
  return status === "accepted" ? "marked done" : status === "rejected" ? "cancelled & re-requested" : status;
}
function _shortDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short" }); }
  catch { return ""; }
}
function PayStatusPill({ s }) {
  const m = {
    not_started: ["bg-slate-100 text-slate-500", "Not started"],
    declared:    ["bg-amber-100 text-amber-700", "Bank transfer declared"],
    reconciled:  ["bg-emerald-100 text-emerald-700", "Reconciled"],
    paid_card:   ["bg-emerald-100 text-emerald-700", "Paid by card"],
  }[s] || ["bg-slate-100 text-slate-500", s];
  return <span className={`text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded ${m[0]}`}>{m[1]}</span>;
}
function QStatusPill({ s }) {
  const m = {
    submitted: ["bg-emerald-100 text-emerald-700", "Submitted"],
    draft:     ["bg-blue-100 text-blue-700", "Draft saved"],
    assigned:  ["bg-amber-100 text-amber-700", "Assigned"],
    not_assigned: ["bg-slate-100 text-slate-400", "—"],
  }[s] || ["bg-slate-100 text-slate-500", s];
  return <span className={`text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded ${m[0]}`}>{m[1]}</span>;
}
function DocLink({ id, label }) {
  return (
    <button type="button"
       onClick={() => downloadAuthed(`/documents/${id}/download`, `${label}.pdf`).catch(() => toast.error("Could not download the document."))}
       className="text-xs font-semibold text-[#100f5d] hover:underline flex items-center gap-1">
      <FileText className="w-3.5 h-3.5" /> {label}
    </button>
  );
}
